import { Axios } from 'shared/utilities/Axios'
import * as loadingActions from 'Store/Loading/actions'
import * as errorActions from 'Store/Error/actions'
import * as c from 'shared/constants'
import { TOKEN_URL, TOKEN_REFRESH_URL, CURRENT_USER_URL } from 'shared/constants/urls'
import { MFA_LOGIN_STEP, parseLoginResponse } from 'Store/Auth/actions/mfaActions'
import { getUserStorageData, removeUserStorageData, setTokenRefreshState, isTokenRefreshing, getRefreshPromise } from 'shared/utilities/storageUtils'
import { retryAuthenticatedRequests } from 'Store/Request/actions'
import { clearCustomSearchSession } from 'Store/AdvancedSearch/actions'

import { handleCatchError } from 'shared/utilities/actionUtils'
import { requestWithAuth } from 'shared/utilities/authUtils'
import { updateAuthLocalStorage } from 'shared/utilities/storageUtils'

import { toast } from 'react-toastify'
import { fireUserLoginEvent, fireUserLogoutEvent } from 'Store/Analytics/actions'
import { getUserBookmarkedProperties, getUserSavedCustomSearches, clearMyDashboard } from 'Store/MyDashboard/actions'

const tokenRefreshDelayMs = () => {
  const accessLeadMs = (c.TOKEN_EXPIRATIONS.access - 60) * 60000
  const refreshLeadMs = c.TOKEN_EXPIRATIONS.refresh * 3600000 - 60 * 60000
  return Math.min(accessLeadMs, refreshLeadMs)
}

export const handleSyncStorage = (storage, dispatch) => {
  if (!storage?.refresh?.token) {
    removeUserStorageData()
    dispatch(clearCustomSearchSession({ redirect: false }))
    dispatch(clearMyDashboard())
    return handleUserLogout()
  }

  return {
    type: c.HANDLE_SYNC_STORAGE,
    data: storage,
    refreshTimeout: setTimeout(
      () => dispatch(refreshTokens(storage.refresh.token)),
      tokenRefreshDelayMs()
    ), // 60 minutes before whichever expires first (access or refresh)
  }
}

export const handleUserLogout = () => {
  return {
    type: c.HANDLE_USER_LOGOUT,
  }
}

export const getUserProfile = ({ showWelcomeToast = false } = {}) => (dispatch, getState, access_token) => {
  dispatch(loadingActions.handleRequest(c.GET_USER_PROFILE))
  dispatch(errorActions.handleClearErrors(c.GET_USER_PROFILE))

  try {
    return Axios.get(CURRENT_USER_URL, {
      headers: {
        authorization: `Bearer ${access_token}`,
      },
    })
      .then(response => {
        dispatch(loadingActions.handleCompletedRequest(c.GET_USER_PROFILE))
        updateAuthLocalStorage(null, null, response.data, dispatch)
        dispatch(handleSyncStorage(getUserStorageData(), dispatch))
        if (showWelcomeToast) {
          toast.success(`Welcome, ${response.data.username}!`)
        }
      })
      .catch(error => {
        handleCatchError(error, c.GET_USER_PROFILE, dispatch)
        dispatch(logoutUser(true))
      })
  } catch (err) {
    const error = { response: { status: 500, data: { results: err.message } } }
    handleCatchError(error, c.GET_USER_PROFILE, dispatch)
  }
}

export const refreshTokens = refresh_token => dispatch => {
  // Check if another tab is already refreshing the token
  if (isTokenRefreshing()) {
    const existingPromise = getRefreshPromise()
    if (existingPromise) {
      return existingPromise
    }
  }

  // Set the refresh state to prevent other tabs from refreshing simultaneously
  const refreshPromise = new Promise((resolve, reject) => {
    setTokenRefreshState(true, refreshPromise)
    
  dispatch(loadingActions.handleRequest(c.GET_TOKEN_REFRESH))
  dispatch(errorActions.handleClearErrors(c.GET_TOKEN_REFRESH))
    
    Axios.post(TOKEN_REFRESH_URL, { refresh: refresh_token })
    .then(response => {
      updateAuthLocalStorage(response.data.access, response.data.refresh, null, dispatch)
      dispatch(loadingActions.handleCompletedRequest(c.GET_TOKEN_REFRESH))

      const storage = getUserStorageData()

      dispatch(handleSyncStorage(storage, dispatch))
        setTokenRefreshState(false, null)
        resolve(storage)
    })
    .catch(error => {
      handleCatchError(error, c.GET_TOKEN_REFRESH, dispatch)
      dispatch(logoutUser(true))
        setTokenRefreshState(false, null)
        reject(error)
      })
    })

  return refreshPromise
}

export const completeLoginWithTokens = (tokenData, postLoginAction) => (dispatch, getState) => {
  const hadSession = !!(getState().auth.user || getUserStorageData()?.user)

  if (tokenData.mfa_setup_recommended && tokenData.mfa_enforcement_starts_at) {
    toast.info(
      `Staff accounts must enable two-factor authentication by ${new Date(
        tokenData.mfa_enforcement_starts_at
      ).toLocaleDateString()}. Set it up under My Dashboard → Security.`
    )
  }

  if (hadSession) {
    dispatch(clearCustomSearchSession({ redirect: true }))
  }

  updateAuthLocalStorage(tokenData.access, tokenData.refresh, null, dispatch)
  dispatch(handleSyncStorage(getUserStorageData(), dispatch))
  return dispatch(requestWithAuth(getUserProfile({ showWelcomeToast: true }), { allowTokenWithoutUser: true }))
    .then(() => {
      dispatch(fireUserLoginEvent())
      dispatch(requestWithAuth(getUserBookmarkedProperties()))
      dispatch(requestWithAuth(getUserSavedCustomSearches()))
      dispatch(retryAuthenticatedRequests())
      if (postLoginAction) postLoginAction()
    })
}

export const loginUser = (data, postLoginAction) => (dispatch, getState) => {
  dispatch(loadingActions.handleRequest(c.GET_TOKEN))
  dispatch(errorActions.handleClearErrors(c.GET_TOKEN))
  return Axios.post(TOKEN_URL, { username: data.username, password: data.password })
    .then(response => {
      dispatch(loadingActions.handleCompletedRequest(c.GET_TOKEN))
      const loginStep = parseLoginResponse(response.data)
      if (loginStep.step === MFA_LOGIN_STEP.COMPLETE && loginStep.tokens?.access) {
        return dispatch(completeLoginWithTokens(loginStep.tokens, postLoginAction))
      }
      return loginStep
    })
    .catch(error => {
      if (error.response?.status === 403 && error.response?.data?.mfa_enrollment_required) {
        dispatch(loadingActions.handleCompletedRequest(c.GET_TOKEN))
        return parseLoginResponse(error.response.data)
      }
      handleCatchError(error, c.GET_TOKEN, dispatch)
      throw error
    })
}

export const logoutUser = (sentToast = false) => dispatch => {
  dispatch(fireUserLogoutEvent())
  removeUserStorageData()
  dispatch(handleUserLogout())
  dispatch(clearCustomSearchSession({ redirect: true }))
  dispatch(clearMyDashboard())
  if (sentToast) {
    toast.info("You've been logged out.")
  }

  dispatch(retryAuthenticatedRequests())
  // window.location = '/'
}
